import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BRAND, ASSETS } from "@/lib/brand";

const API = `${window.location.origin}/api`;

const initial = {
  nome: "",
  azienda: "",
  email: "",
  canale: "Ho.Re.Ca.",
  messaggio: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.azienda || !form.email || !form.messaggio) {
      toast.error("Compila tutti i campi richiesti.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/allocations`, form);
      setSent(true);
      setForm(initial);
      toast.success("Richiesta inviata. Ti ricontatteremo a breve.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Si è verificato un errore. Riprova più tardi.";
      toast.error(typeof msg === "string" ? msg : "Errore nell'invio.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contatti"
      data-testid="contatti-section"
      className="relative bg-white"
    >
      <div className="max-w-[1480px] mx-auto px-6 sm:px-12 lg:px-20 py-24 sm:py-32 lg:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left intro */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-6 mb-8">
              <span className="section-num text-3xl">13</span>
              <div className="h-px w-12 bg-[#0A0A0A]" />
              <span className="overline text-[10px]">Accesso Esclusivo</span>
            </div>

            <h2
              className="font-serif-display leading-[0.95] text-[#0A0A0A]"
              style={{ fontSize: "clamp(2.6rem, 6vw, 5.5rem)" }}
            >
              Richiedi
              <br />
              <span className="italic font-light">Allocazioni.</span>
            </h2>

            <p className="mt-8 text-[16px] leading-[1.9] text-[#2C2C2C] font-light max-w-md">
              Le referenze La Valle degli Iblei sono disponibili esclusivamente
              in <em className="font-serif-display">allocazioni limitate</em>{" "}
              per il canale Ho.Re.Ca. Premium e Partner Export Selezionati.
            </p>

            <div className="mt-12 space-y-6">
              <ContactLine label="Email" value={BRAND.email} link={`mailto:${BRAND.email}`} />
              <ContactLine label="Territorio" value="Sicilia Sud-Orientale, Italia" />
              <ContactLine label="Canale" value="Ho.Re.Ca. · Export Selezionato" />
            </div>

            <img
              src={ASSETS.logo}
              alt="Stemma La Valle degli Iblei"
              className="mt-16 h-20 w-20 object-contain opacity-90"
            />
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="lg:col-span-7 lg:pl-12 lg:border-l lg:border-[#EAEAEA]"
            data-testid="contact-form-wrapper"
          >
            {sent ? (
              <div
                className="h-full flex flex-col items-start justify-center min-h-[400px]"
                data-testid="contact-success"
              >
                <span className="overline text-[10px] text-[#C4A671]">
                  Richiesta Ricevuta
                </span>
                <h3 className="font-serif-display text-4xl sm:text-5xl mt-4 text-[#0A0A0A] leading-tight">
                  Grazie.
                  <br />
                  <em className="text-[#63171B]">A presto.</em>
                </h3>
                <p className="mt-6 text-[#2C2C2C] font-light max-w-md leading-relaxed">
                  La tua richiesta è stata inviata al nostro ufficio commerciale.
                  Ti contatteremo personalmente a breve.
                </p>
                <button
                  data-testid="contact-reset-btn"
                  className="btn-ghost mt-10"
                  onClick={() => setSent(false)}
                >
                  Nuova Richiesta <span>→</span>
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
                data-testid="contact-form"
              >
                <Field label="Nome e Cognome" required>
                  <input
                    data-testid="contact-nome"
                    className="editorial-field"
                    type="text"
                    value={form.nome}
                    onChange={update("nome")}
                    placeholder="Mario Rossi"
                    required
                  />
                </Field>

                <Field label="Azienda / Ristorante" required>
                  <input
                    data-testid="contact-azienda"
                    className="editorial-field"
                    type="text"
                    value={form.azienda}
                    onChange={update("azienda")}
                    placeholder="Ragione sociale"
                    required
                  />
                </Field>

                <Field label="Email" required>
                  <input
                    data-testid="contact-email"
                    className="editorial-field"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="email@azienda.com"
                    required
                  />
                </Field>

                <Field label="Canale" required>
                  <select
                    data-testid="contact-canale"
                    className="editorial-field appearance-none cursor-pointer"
                    value={form.canale}
                    onChange={update("canale")}
                  >
                    <option value="Ho.Re.Ca.">Ho.Re.Ca. Premium</option>
                    <option value="Export">Export Selezionato</option>
                  </select>
                </Field>

                <div className="md:col-span-2">
                  <Field label="Messaggio" required>
                    <textarea
                      data-testid="contact-messaggio"
                      className="editorial-field resize-none"
                      rows={4}
                      value={form.messaggio}
                      onChange={update("messaggio")}
                      placeholder="Raccontaci la tua attività e i volumi indicativi..."
                      required
                    />
                  </Field>
                </div>

                <div className="md:col-span-2 mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                  <p className="text-xs text-[#5C5C5C] font-light max-w-sm leading-relaxed">
                    Inviando questa richiesta accetti di essere contattato dal
                    nostro ufficio commerciale per finalità di allocazione.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-ink"
                    data-testid="submit-allocation"
                  >
                    {submitting ? "Invio in corso…" : "Richiedi Informazioni"}
                    <span aria-hidden>→</span>
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="overline text-[9.5px] block mb-1">
        {label} {required && <span className="text-[#63171B]">*</span>}
      </span>
      {children}
    </label>
  );
}

function ContactLine({ label, value, link }) {
  const Inner = (
    <div className="flex items-baseline justify-between gap-6 border-b border-[#EAEAEA] pb-3">
      <span className="overline text-[9.5px]">{label}</span>
      <span className="font-serif-display text-base text-[#0A0A0A]">{value}</span>
    </div>
  );
  return link ? (
    <a
      href={link}
      data-testid={`contact-link-${label.toLowerCase()}`}
      className="block hover:opacity-70 transition"
    >
      {Inner}
    </a>
  ) : (
    Inner
  );
}
